// Auth helper: MSAL runtime loader and token helpers
//
// Purpose:
// - Lazily import `@azure/msal-browser` so the MSAL bundle is only loaded when necessary.
// - Retrieve Azure AD configuration from the backend (if provided) and fall back to Vite env vars.
// - Expose small, well-documented helpers used by the app: `initializeMsal`, `getActiveAccount`,
//   `login`, `logout`, `switchAccount`, `ensureReauth`, and `getAccessTokenSilent`.
//
// Notes on maintainability & security:
// - The MSAL instance is initialized at runtime and stored in a module-scoped variable.
// - We avoid shipping MSAL types/statics to the app entry so the bundle can be code-split.
// - This module intentionally keeps behavior identical to the previous implementation but is
//   reorganized and documented for clarity.

import { resolveConfig, getCachedConfig } from './config';

// Runtime-loaded symbols from @azure/msal-browser
let PublicClientApplication: any = null;
let EventType: any = null;
let InteractionRequiredAuthError: any = null;
let msalInstance: any = null;

let initialized = false;
let initializing: Promise<void> | null = null;
let reauthInProgress = false;

async function ensureInitialized() {
  if (initialized) return;
  if (!initializing) {
    initializing = (async () => {
      // Retrieve runtime configuration (may fetch backend public config)
      const cfg = await resolveConfig();

      const msalConfig = {
        auth: {
          clientId: cfg.clientId,
          authority: cfg.authority,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false
        }
      };

      // Dynamically import the MSAL library only when needed
      const msal = await import('@azure/msal-browser');
      PublicClientApplication = msal.PublicClientApplication;
      EventType = msal.EventType;
      InteractionRequiredAuthError = msal.InteractionRequiredAuthError;

      msalInstance = new PublicClientApplication(msalConfig);

      // Event callback: keep active account in sync and clear reauth flag
      msalInstance.addEventCallback((event: any) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as any;
          msalInstance.setActiveAccount(payload.account);
          reauthInProgress = false;
        }
        if (event.eventType === EventType.LOGIN_FAILURE) {
          reauthInProgress = false;
        }
      });

      // msal.initialize is optional on some bundles; ignore initialize errors
      if (typeof msalInstance.initialize === 'function') {
        try { await msalInstance.initialize(); } catch (e) { console.warn('msal initialize warning', e); }
      }

      initialized = true;
    })();
  }
  await initializing;
}

// Called from App startup to handle redirect responses and initialize active account
export async function initializeMsal() {
  await ensureInitialized();
  try {
    const result = await msalInstance.handleRedirectPromise();
    if (result) msalInstance.setActiveAccount(result.account);
    else {
      const existing = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (existing) msalInstance.setActiveAccount(existing);
    }
  } catch (e) {
    console.error('MSAL init error', e);
  }
}

// Return the currently active account (or null). This helper avoids other modules importing msal-browser.
export function getActiveAccount() {
  if (!msalInstance) return null;
  return msalInstance.getActiveAccount() || (msalInstance.getAllAccounts && msalInstance.getAllAccounts()[0]) || null;
}

export async function login(forceAccountSelection: boolean = true) {
  await ensureInitialized();
  const cfg = getCachedConfig() || await resolveConfig();
  const request: any = { scopes: cfg.loginScopes };
  if (forceAccountSelection) request.prompt = 'select_account';
  await msalInstance.loginRedirect(request);
}

export async function switchAccount() {
  await ensureInitialized();
  const cfg = getCachedConfig() || await resolveConfig();
  await msalInstance.loginRedirect({ scopes: cfg.loginScopes, prompt: 'select_account' });
}

export async function logout() {
  await ensureInitialized();
  const account = msalInstance.getActiveAccount();
  if (account) await msalInstance.logoutRedirect({ account });
  else await msalInstance.logoutRedirect();
}

export async function ensureReauth(promptAccountSelection: boolean = false) {
  await ensureInitialized();
  if (reauthInProgress) return;
  reauthInProgress = true;
  const cfg = getCachedConfig() || await resolveConfig();
  const request: any = { scopes: cfg.loginScopes };
  if (promptAccountSelection) request.prompt = 'select_account';
  await msalInstance.loginRedirect(request);
}

export async function getAccessTokenSilent(scopes?: string[]): Promise<string | null> {
    await ensureInitialized();
    const cfg = getCachedConfig() || await resolveConfig();
    const usedScopes = scopes && scopes.length > 0 ? scopes : cfg.loginScopes;

    const account = msalInstance.getActiveAccount() || (msalInstance.getAllAccounts && msalInstance.getAllAccounts()[0]);
    if (!account) {
        // No account in session -> trigger reauth (fire-and-forget) and return null
        ensureReauth(false);
        return null;
    }
    try {
        const r = await msalInstance.acquireTokenSilent({ account, scopes: usedScopes });
        return r.accessToken;
    } catch (err) {
        // Try again forcing refresh
        try {
            const r2 = await msalInstance.acquireTokenSilent({ account, scopes: usedScopes, forceRefresh: true });
            return r2.accessToken;
        } catch (err2) {
            // If interaction is required (expired/consent), trigger re-authentication
            if (InteractionRequiredAuthError && err2 instanceof InteractionRequiredAuthError) {
                ensureReauth(false);
            }
            return null;
        }
    }
}

// Acquire both an API access token (using loginScopes) and a Microsoft Graph access token (using graphScopes).
// The API token is used for calling our backend; the Graph token can be forwarded so the backend can call Graph.
export async function getApiAndGraphTokens(): Promise<{ apiToken: string | null; graphToken: string | null }> {
  await ensureInitialized();
  const cfg = getCachedConfig() || await resolveConfig();

  const account = msalInstance.getActiveAccount() || (msalInstance.getAllAccounts && msalInstance.getAllAccounts()[0]);
  if (!account) {
    ensureReauth(false);
    return { apiToken: null, graphToken: null };
  }

  let apiToken: string | null = null;
  try {
    const apiResult = await msalInstance.acquireTokenSilent({ account, scopes: cfg.loginScopes });
    apiToken = apiResult.accessToken;
  }
  catch (e) {
    apiToken = null;
  }

  let graphToken: string | null = null;
  if (cfg.graphScopes && cfg.graphScopes.length > 0) {
    try {
      const graphResult = await msalInstance.acquireTokenSilent({ account, scopes: cfg.graphScopes });
      graphToken = graphResult.accessToken;
    }
    catch (e) {
      graphToken = null;
    }
  }

  if (!apiToken) {
    ensureReauth(false);
  }

  return { apiToken, graphToken };
}
